import { Link } from "react-router-dom";
import { SIGNUP_STATUS_LABELS } from "../../api";
import type { Signup } from "../../api";
import { Badge, Button, Modal } from "../../components";
import { formatDateTime } from "../../format";
import { esnEntreprisePath } from "../../shell";
import styles from "./Esn.module.css";
import { TON_STATUT } from "./inscriptionStatut";

export type EsnInscriptionDetailProps = {
  signup: Signup | null;
  onClose: () => void;
  onAccept: (signup: Signup) => void;
  onReject: (signup: Signup) => void;
};

/**
 * La fiche d'une demande : tout ce que le formulaire a dit, et la décision si
 * elle est prise. Les deux actions ne s'offrent qu'à une demande en attente.
 */
export function EsnInscriptionDetail({ signup, onClose, onAccept, onReject }: EsnInscriptionDetailProps) {
  const enAttente = signup?.status === "pending";
  return (
    <Modal
      open={signup !== null}
      onClose={onClose}
      title={signup?.company_name ?? "Demande"}
      footer={
        enAttente && signup !== null ? (
          <>
            <Button variant="danger" onClick={() => onReject(signup)}>
              Refuser
            </Button>
            <Button variant="primary" onClick={() => onAccept(signup)}>
              Accepter
            </Button>
          </>
        ) : (
          <Button variant="ghost" onClick={onClose}>
            Fermer
          </Button>
        )
      }
    >
      {signup !== null && (
        <dl className={styles.fiche}>
          <div className={styles.ficheLigne}>
            <dt>Statut</dt>
            <dd>
              <Badge tone={TON_STATUT[signup.status]} dot>
                {SIGNUP_STATUS_LABELS[signup.status]}
              </Badge>
            </dd>
          </div>
          <div className={styles.ficheLigne}>
            <dt>Contact</dt>
            <dd>
              {signup.contact_name}
              <span className={styles.contactSecondaire}> · {signup.contact_title}</span>
            </dd>
          </div>
          <div className={styles.ficheLigne}>
            <dt>E-mail</dt>
            <dd>
              <a href={`mailto:${signup.email}`}>{signup.email}</a>
            </dd>
          </div>
          <div className={styles.ficheLigne}>
            <dt>Téléphone</dt>
            <dd>
              <a href={`tel:${signup.phone.replace(/\s/g, "")}`}>{signup.phone}</a>
            </dd>
          </div>
          <div className={styles.ficheLigne}>
            <dt>SIRET</dt>
            <dd>{signup.siret ?? <span className={styles.contactSecondaire}>non renseigné</span>}</dd>
          </div>
          <div className={styles.ficheLigne}>
            <dt>Site</dt>
            <dd>
              {signup.website ? (
                <a href={signup.website} target="_blank" rel="noopener noreferrer">
                  {signup.website}
                </a>
              ) : (
                <span className={styles.contactSecondaire}>non renseigné</span>
              )}
            </dd>
          </div>
          <div className={styles.ficheLigne}>
            <dt>Reçue le</dt>
            <dd>{formatDateTime(signup.created_at)}</dd>
          </div>
          <div className={styles.ficheLigne}>
            <dt>Besoin</dt>
            <dd className={styles.besoin}>{signup.message}</dd>
          </div>
          {signup.decided_at !== null && (
            <div className={styles.ficheLigne}>
              <dt>Décidée le</dt>
              <dd>{formatDateTime(signup.decided_at)}</dd>
            </div>
          )}
          {signup.rejection_reason !== null && (
            <div className={styles.ficheLigne}>
              <dt>Motif du refus</dt>
              <dd className={styles.besoin}>{signup.rejection_reason}</dd>
            </div>
          )}
          {signup.company_id !== null && (
            <div className={styles.ficheLigne}>
              <dt>Entreprise</dt>
              <dd>
                <Link to={esnEntreprisePath(signup.company_id)}>Ouvrir la fiche de l'entreprise</Link>
              </dd>
            </div>
          )}
        </dl>
      )}
    </Modal>
  );
}
